import { Injectable, NgZone } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { filter } from 'rxjs';

import { PushContent } from '../shared/interfaces/pushContent';
import { LocalStorageService } from './local-storage.service';
import { PushNotificationModalComponent, PushNotificationModalInput } from '../shared/modals/push-notification-modal/push-notification-modal.component';

/**
 * Chiave di `sessionStorage` per la notifica push in attesa di una sessione attiva.
 *
 * <p>`sessionStorage` e NON `localStorage`: il contenuto della notifica ha lo stesso orizzonte di
 * vita dell'access token (anch'esso in `sessionStorage`, vedi `LocalStorageService`) e può contenere
 * dati personali. Metterlo in `localStorage` lo farebbe sopravvivere alla chiusura del browser e lo
 * renderebbe leggibile in una sessione successiva, potenzialmente di un altro utente sullo stesso
 * dispositivo — cioè esattamente ciò che la scelta di tenere i token fuori da `localStorage` evita.
 */
const PUSH_IN_ATTESA_KEY = 'push_in_attesa';

/**
 * Gestione del contenuto di una notifica push consegnata dal guscio nativo.
 *
 * <p><b>Il nodo è l'ordine tra push e login.</b> Il nativo consegna il payload a
 * `window.onPushReceived` appena la SPA è pronta (`appInitEnd`), a prescindere dallo stato di
 * autenticazione. Aprire subito la modale in quel momento significa mostrarla SOPRA la pagina di
 * login: se l'utente la chiude per autenticarsi, il contenuto è perso per sempre, perché l'unica
 * coda esistente è nativa e vale solo per "prima che la SPA sia avviata".
 *
 * <p>Perciò la modale è subordinata alla sessione: senza access token il contenuto va in attesa
 * (in `sessionStorage`, così sopravvive a un reload della web app) e viene mostrato appena la
 * sessione diventa attiva — al login riuscito o al recovery al bootstrap, dai punti che già
 * chiamano `PushTokenService.registraTokenPendente$()`. Il contenuto è consumato all'apertura
 * effettiva della modale: non viene riproposto al login successivo.
 *
 * <p><b>Il secondo nodo è l'ordine tra push e navigazioni automatiche.</b> Non basta presentare
 * dopo il `navigateRoot` del login: la pagina di destinazione può innescare a sua volta un salto
 * automatico (`saltaAiBandiSeRidondante` in `home.page.ts`, e gli analoghi in `bandi.page.ts` e
 * `pratiche-tipologia.page.ts`, che saltano le schermate a un solo elemento), deciso solo quando
 * risponde la chiamata HTTP del catalogo — cioè un attimo DOPO che la modale è comparsa. Ogni
 * navigazione attraversa `ModalGuard`, che via `AutocloseOverlaysService` chiude qualunque overlay
 * in cima allo stack: la modale push veniva così chiusa da sola dopo un istante, e per soprammercato
 * la navigazione veniva annullata (il guard restituisce `canDeactivate: false` quando ha chiuso
 * qualcosa), lasciando l'utente fermo con la notifica svanita.
 *
 * <p>La soluzione NON è attendere: qualunque attesa sarebbe una scommessa sul tempo di risposta
 * della rete, e su una rete lenta il salto arriverebbe comunque a modale già aperta. La modale si
 * dichiara invece <b>compatibile con le navigazioni automatiche</b> (`isAutoSkipSafe` nei suoi
 * `componentProps`) e i tre auto-skip dichiarano di essere automatici (`state: {autoSkip: true}`):
 * `AutocloseOverlaysService` incrocia le due dichiarazioni e, quando coincidono, lascia la modale
 * aperta E lascia passare la navigazione. Il dato è sincrono e disponibile nell'istante in cui il
 * guard decide, quindi non c'è finestra temporale né corsa: la modale resta al suo posto mentre
 * sotto cambia la pagina, perché gli overlay Ionic vivono su `ion-app`, fuori dal router-outlet.
 *
 * <p>Il tasto indietro continua a chiuderla: il back non passa `autoSkip`, quindi ricade nel ramo
 * normale del servizio. È la ragione per cui non si usa `isNotDismissableWithBack`, che bloccherebbe
 * anche il back e — restituendo `canDeactivate: false` — impedirebbe per sempre il salto.
 *
 * <p><b>Il terzo nodo è la deduplica.</b> Serve, perché lo stesso payload può essere consegnato due
 * volte a breve distanza, ma deve valere quanto la modale e non quanto l'app: vedi `notificaInScena`.
 * Attenzione a `computeFallbackKey`, che è il ramo <i>normale</i> e non l'eccezione — il backend
 * pubblica l'id dell'invio come `invio_id`, non come `notificationId`, quindi `notificationId` arriva
 * quasi sempre assente e il titolo resta l'unico campo discriminante della chiave.
 */
@Injectable({ providedIn: 'root' })
export class PushContentService {

  private isModalOpen = false;

  /**
   * Chiave della notifica attualmente in scena, per scartare una <b>riconsegna</b> dello stesso
   * payload mentre la modale è ancora aperta.
   *
   * <p><b>Vive quanto la modale, non quanto l'app.</b> Prima era un campo mai azzerato su un servizio
   * `providedIn: 'root'`: la prima notifica lo valorizzava e ogni notifica successiva con la stessa
   * chiave veniva scartata in silenzio per il resto della sessione — si tornava a vedere le modali
   * solo chiudendo e riaprendo l'app, cioè ricreando il singleton. E le chiavi collidono facilmente:
   * `notificationId` è opzionale nel payload, quindi si ricade su `computeFallbackKey`, dove l'unico
   * campo davvero discriminante è il titolo. Due avvisi con lo stesso titolo — o lo stesso avviso
   * rimandato — producono chiavi identiche.
   *
   * <p>Si alza e si abbassa insieme a `isModalOpen`, dentro `openModal`: la protezione resta dov'era
   * utile — la doppia consegna ravvicinata dello stesso payload — senza diventare un blocco
   * permanente. Scriverla in `mostra()`, prima del tentativo di apertura, la farebbe mentire quando
   * l'apertura viene saltata perché un'altra modale è già in scena.
   */
  private notificaInScena: string | null = null;

  /**
   * Presentazione in corso, condivisa dalle chiamate concorrenti. Vedi `mostraNotificaInAttesa`
   * per il motivo per cui non basta il consumo dallo storage.
   */
  private presentazioneInCorso: Promise<void> | null = null;

  constructor(
    private modalCtrl: ModalController,
    private router: Router,
    private zone: NgZone,
    private localStorageService: LocalStorageService,
  ) {
    // La notifica in attesa si azzera quando la sessione cade, qualunque sia la via: logout
    // esplicito, token scaduto (401), accesso negato (403), recovery fallito al bootstrap. Ci si
    // aggancia al segnale di sessione — come fa `DocumentiComuneGateService` — invece di chiamare il reset da
    // ogni punto di teardown: sono quattro e sparsi, e basta dimenticarne uno perché la notifica
    // di un utente venga mostrata al successivo che accede sullo stesso dispositivo.
    this.localStorageService.checkLoggedUser$
      .pipe(filter((loggato) => !loggato))
      .subscribe(() => this.resetInAttesa());
  }

  /**
   * Riceve il payload dal guscio nativo: apre la modale se c'è una sessione attiva, altrimenti
   * mette il contenuto in attesa del login.
   */
  async gestisciNotifichePush(pushContent: PushContent): Promise<void> {
    if (!this.localStorageService.isLogged) {
      console.log('[PushContent] notifica ricevuta senza sessione attiva: modale differita al login');
      this.salvaInAttesa(pushContent);
      return;
    }
    await this.mostra(pushContent);
  }

  /**
   * Mostra la notifica messa in attesa, se presente. Da chiamare quando la sessione diventa attiva
   * (login riuscito, recovery al bootstrap): copre il caso in cui la push è arrivata prima
   * dell'autenticazione.
   *
   * <p>La presentazione è immediata: non si attende nulla, perché la modale è immune ai salti
   * automatici che prima la richiudevano (vedi la nota di classe).
   *
   * <p>Il contenuto è rimosso dallo storage all'apertura della modale e non appena letto: è
   * l'apertura a segnare il "mostrato", e `resetInAttesa()` scatta comunque alla caduta della
   * sessione.
   *
   * <p>Le chiamate concorrenti condividono la stessa presentazione invece di avviarne una ciascuna:
   * i chiamanti sono quattro (login credenziali, login passkey, callback SPID, recovery al
   * bootstrap) e due possono sovrapporsi. Poiché il payload viene consumato solo all'apertura
   * della modale, senza questa condivisione la seconda chiamata rileggerebbe lo stesso contenuto
   * e ne avvierebbe una seconda presentazione in parallelo.
   */
  async mostraNotificaInAttesa(): Promise<void> {
    if (this.presentazioneInCorso) {
      await this.presentazioneInCorso;
      return;
    }
    const inAttesa = this.leggiInAttesa();
    if (!inAttesa) {
      return;
    }
    this.presentazioneInCorso = this.mostra(inAttesa)
      .finally(() => (this.presentazioneInCorso = null));
    await this.presentazioneInCorso;
  }

  /** `true` se c'è una notifica in attesa di una sessione attiva. */
  get haNotificaInAttesa(): boolean {
    return !!this.leggiInAttesa();
  }

  /**
   * Azzera la notifica in attesa: il contenuto di un utente non va mostrato al successivo che
   * accede sullo stesso dispositivo.
   */
  resetInAttesa(): void {
    try {
      sessionStorage.removeItem(PUSH_IN_ATTESA_KEY);
    } catch {
      // best-effort: sessionStorage non disponibile → non c'era nulla da rimuovere
    }
  }

  /**
   * Presenta la modale rientrando in NgZone: la chiamata originaria arriva da fuori Angular
   * (`evaluateJavascript`/`callAsyncJavaScript` del guscio nativo), quindi senza il rientro la
   * modale verrebbe creata fuori dalla zona e la change detection non scatterebbe.
   *
   * <p>Il `Promise` restituito da `zone.run` è atteso: prima non lo era, e il metodo risolveva
   * appena la callback partiva, cioè prima che la modale esistesse. Ora che i chiamanti post-login
   * attendono la presentazione (per mostrarla dopo `navigateRoot`), l'attesa deve essere reale.
   */
  private async mostra(pushContent: PushContent): Promise<void> {
    await this.zone.run(async () => {
      const input = this.normalize(pushContent);

      const key = input.notificationId ?? this.computeFallbackKey(input);
      if (key && this.notificaInScena === key) return;

      await this.openModal(input, key);
    });
  }

  /**
   * Persiste il payload grezzo così com'è arrivato dal nativo, senza aggiungere nulla: la modale
   * ricalcola i campi derivati (route, dettagli) al momento della presentazione. Best-effort — se
   * `sessionStorage` non è scrivibile la notifica va perduta, ma non si rompe nulla a monte.
   */
  private salvaInAttesa(pushContent: PushContent): void {
    try {
      sessionStorage.setItem(PUSH_IN_ATTESA_KEY, JSON.stringify(pushContent ?? {}));
    } catch {
      // best-effort: sessionStorage non disponibile → nessuna notifica differita
    }
  }

  private leggiInAttesa(): PushContent | null {
    try {
      const raw = sessionStorage.getItem(PUSH_IN_ATTESA_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as PushContent) : null;
    } catch {
      return null;
    }
  }

  /**
   * @param key chiave della notifica, registrata come "in scena" solo se si arriva davvero a
   *            presentare: impostarla prima del tentativo la farebbe mentire nel caso in cui
   *            l'apertura viene saltata perché un'altra modale è già a schermo.
   */
  private async openModal(input: PushNotificationModalInput, key: string | null): Promise<void> {
    if (this.isModalOpen) return;
    this.isModalOpen = true;
    this.notificaInScena = key;

    // `finally` e non un ripristino in coda: se la creazione o la presentazione falliscono (Ionic
    // rigetta se l'overlay viene rimosso mentre è in transizione — possibile proprio perché qui si
    // convive con guard che chiudono overlay), il flag resterebbe alzato per sempre e nessuna
    // notifica push potrebbe più aprirsi per tutta la vita del servizio, che è un singleton.
    try {
      const modal = await this.modalCtrl.create({
        component: PushNotificationModalComponent,
        // `isAutoSkipSafe`: dichiara a `AutocloseOverlaysService` che questa modale può restare
        // aperta durante una navigazione automatica. Vedi la nota di classe.
        componentProps: { ...input, isAutoSkipSafe: true },
        cssClass: 'push-notification-modal',
        backdropDismiss: true,
        htmlAttributes: { 'aria-labelledby': 'modal-title' }
      });

      await modal.present();

      // Consumo del payload DOPO che la modale è realmente in scena: se `create()` o `present()`
      // fallissero, il contenuto resterebbe in storage e la notifica sarebbe ancora recuperabile
      // al prossimo tentativo, invece di essere perduta senza essere mai apparsa.
      this.resetInAttesa();

      const result = await modal.onWillDismiss<{ action?: 'open'; route?: string }>();

      if (result?.data?.action === 'open' && result.data.route) {
        await this.router.navigateByUrl(result.data.route);
      }
    } finally {
      this.isModalOpen = false;
      // Insieme al flag: se `create()`/`present()` fallisce, la chiave non deve restare a bloccare
      // la stessa notifica per il resto della sessione (era il difetto di prima). Rilasciarla qui
      // significa che la prossima notifica — anche identica — trova il campo pulito.
      this.notificaInScena = null;
    }
  }

  private normalize(pushContent: PushContent): PushNotificationModalInput {
    const title = (pushContent?.title ?? '').trim();
    const message = (pushContent?.message ?? '').trim();

    const extra = pushContent?.extraData ?? undefined;

    const notificationId = (extra?.notificationId ?? '').trim() || undefined;
    const notificationType = (extra?.notificationType ?? '').trim() || 'GENERIC';

    const bodyParsed = this.tryParseJson(extra?.body);
    const route = this.routeFor(extra);

    // UI fields
    const detailsLines: Array<{ label: string; value?: string }> = [
      { label: 'Type', value: notificationType },
      { label: 'ID', value: notificationId },
      { label: 'Destinazione', value: extra?.tipo },
      { label: 'Risorsa', value: extra?.id },
      { label: 'Timestamp', value: extra?.timestamp },
    ].filter(x => !!x.value) as any;

    return {
      // Titolo vuoto quando il payload non ne porta uno: il ripiego lo mette la modale, che ha lo
      // scope di traduzione (qui saremmo costretti a scrivere una lingua sola).
      modalTitle: title || extra?.title || '',
      modalMessage: message || undefined,
      notificationId,
      notificationType,
      timestamp: extra?.timestamp,
      bodyRaw: extra?.body,
      bodyParsed,
      route,
      detailsLines,
      raw: pushContent,
    };
  }

  /**
   * Schermata da aprire per ogni tipo di notifica, con l'identificativo della risorsa.
   *
   * È una tabella e non uno `switch` per una ragione di sicurezza, non di stile: la versione
   * precedente accettava un tipo `NAVIGATE` che portava con sé la rotta già formata, quindi un
   * payload malformato — o manomesso — poteva far navigare l'app ovunque. Qui le destinazioni
   * possibili sono solo quelle scritte sotto, e il payload può scegliere fra queste, non
   * aggiungerne.
   *
   * Le chiavi corrispondono all'enum `PushDeepLinkTipo` del backend: sono contratto, non testo
   * libero. Un tipo che non compare qui non produce nessuna navigazione (vedi `routeFor`).
   */
  private static readonly DEEP_LINK_ROUTES: Record<string, (id: string) => string> = {
    PRATICA: id => `/pages/mie-pratiche/${encodeURIComponent(id)}`,
    // L'esito della graduatoria si legge nel dettaglio della propria domanda: nell'app non
    // esiste una pagina "graduatoria" per il cittadino.
    GRADUATORIA: id => `/pages/mie-pratiche/${encodeURIComponent(id)}`,
    VOUCHER: id => `/pages/miei-voucher/${encodeURIComponent(id)}`,
    // La pagina dei pagamenti mostra un figlio alla volta e lo sceglie da uno stato interno,
    // non dalla rotta: l'id passa come parametro perché la pagina possa preselezionare quello
    // a cui l'avviso si riferisce, invece di aprirsi su quello di default.
    STUDENTE_MENSA: id => `/pages/pagamenti-mensa?studente=${encodeURIComponent(id)}`,
  };

  /**
   * Rotta da aprire al tocco, oppure `null` se la notifica non ne ha una.
   *
   * `null` non è un caso d'errore: è la condizione normale di un avviso di solo testo, e la
   * modale in quel caso mostra il solo bottone di chiusura. Si torna `null` anche quando il tipo
   * è sconosciuto — un'app non ancora aggiornata che riceve un tipo nuovo mostra il messaggio
   * senza navigare, invece di tentare una rotta inesistente.
   */
  private routeFor(extra?: PushContent['extraData']): string | null {
    const tipo = (extra?.tipo ?? '').trim();
    const id = (extra?.id ?? '').trim();
    if (!tipo || !id) return null;

    const buildRoute = PushContentService.DEEP_LINK_ROUTES[tipo];
    return buildRoute ? buildRoute(id) : null;
  }

  private tryParseJson(value?: string): any | null {
    if (!value) return null;
    const v = value.trim();
    if (!v) return null;
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }

  /**
   * Chiave di riconoscimento usata solo quando la notifica non porta un proprio identificativo.
   *
   * Include la destinazione (tipo e risorsa) perché senza di essa due avvisi diversi sullo stesso
   * bando — mettiamo l'esito di due domande — collasserebbero nella stessa chiave, e il secondo
   * verrebbe scartato come doppione senza mai essere mostrato.
   */
  private computeFallbackKey(input: PushNotificationModalInput): string | null {
    const destinazione = `${input.raw?.extraData?.tipo ?? ''}|${input.raw?.extraData?.id ?? ''}`;
    const s = `${input.notificationType}|${input.modalTitle}|${input.timestamp ?? ''}|${destinazione}`;
    return s.length ? s : null;
  }
}
